import React, {PropsWithChildren, useMemo} from 'react';
import {FormikErrors, FormikSharedConfig, FormikValues, withFormik} from 'formik';
import {Box, Button, Typography} from '@mui/material';
import * as Yup from 'yup';
import {Moment} from 'moment';
import {isObject, merge, transform} from 'lodash';
import {useDebugContext} from '../../contexts/DebugProvider';
import {FormField, FieldValue} from './AutoFieldNew';
import {cleanForCuro} from '../../utils/cleanString';
import {useAutoForm} from './useAutoForm';
import {dotize} from '../../utils/flattenObject';
import {themeColors} from '../../theme/Theme';
import {SaltusAny} from '../../utils/SaltusAny';
import {UPDATES} from '../../shared/UPDATES';

export type moneyFieldValueType = number | string | undefined;
export type dateFieldValueType = Date | undefined | Moment | null;

export type RecursivePartial<T> = {
  [P in keyof T]?: T[P] extends (infer U)[]
    ? RecursivePartial<U>[]
    : T[P] extends Record<string, unknown>
    ? RecursivePartial<T[P]>
    : T[P];
};
export interface AutoFormProps<
  FormSchema,
  CustomFormField extends FormField = FormField
> {
  formFields: CustomFormField[];
  initialValueOverrides?: RecursivePartial<FormSchema>;
  submit?: (values: FormSchema) => void;
  validationSchema?: Yup.SchemaOf<any>;
  validate?: (values: FormSchema) => FormikErrors<any>;
}

type OtherFixedProps = {
  debugPosition?: 'left' | 'top' | 'bottom' | 'right';
};

type OtherRuntimeProps = {debugChildren?: React.ReactNode; debugChildrenValue?: string};

function deepOmitIds(obj: Record<string, any>) {
  // const keysToOmitIndex = keyBy(Array.isArray(keysToOmit) ? keysToOmit : [keysToOmit]); // create an index object of the keys that should be omitted

  // eslint-disable-next-line @typescript-eslint/no-shadow
  function omitFromObject(obj: SaltusAny) {
    // the inner function which will be called recursivley
    return transform<string, any>(obj, (result, value, key: string) => {
      // transform to a new object
      if (isObject(value)) {
        result[key] = omitFromObject(value);
      } else if (typeof value !== 'string') {
        result[key] = value;
      } else if (value.length !== 'd9e9a092-ab4a-4941-90fb-cb466b858f65'.length) {
        result[key] = value;
      }
    });
  }

  return omitFromObject(obj); // return the inner function result
}

export const AutoFormDebug: React.FC<{
  debugChildren?: React.ReactNode;
  debugChildrenValue?: string;
}> = (props) => {
  const {values, errors, setValues} = useAutoForm<Record<string, FieldValue>>();

  return (
    <div>
      <Box>
        <Box display="flex" flexDirection="row">
          <pre style={{fontSize: '0.9em', lineHeight: 1.2, flex: 1}}>
            <Typography color={themeColors.backgroundDark}>
              <strong>FORM VALUES:</strong>
              <Button
                onClick={() =>
                  navigator.clipboard.writeText(JSON.stringify(values, null, 2))
                }
              >
                Copy to clipboard
              </Button>
              <Button
                onClick={(e) =>
                  navigator.clipboard.readText().then((data) => {
                    const json = JSON.parse(data);
                    const newVals = deepOmitIds(json);
                    if (e.altKey) {
                      setValues(UPDATES._1000, newVals);
                    } else {
                      setValues(UPDATES._1000, merge({}, values, newVals));
                    }
                  })
                }
              >
                Insert from clipboard
              </Button>
            </Typography>{' '}
            {JSON.stringify(values, null, 2)}
            <br />
            <Typography color={themeColors.coral}>
              <strong>ERRORS:</strong>{' '}
            </Typography>
            {JSON.stringify(errors, null, 2)}
            <br />
            <strong>DOT NOTATION:</strong>{' '}
            <pre>{JSON.stringify(dotize.convert(values), null, 2)}</pre>
          </pre>

          {props.debugChildren ? (
            <pre style={{fontSize: '0.9em', lineHeight: 1.2, flex: 1}}>
              <strong>SERVER VALUES:</strong>
              {props.debugChildrenValue ? (
                <Button
                  onClick={() => navigator.clipboard.writeText(props.debugChildrenValue!)}
                >
                  Copy to clipboard
                </Button>
              ) : null}
              {props.debugChildren}
              <br />
            </pre>
          ) : null}
        </Box>
      </Box>
    </div>
  );
};

// READ INPUTS BEFORE USING:
function withAutoFormNew<
  FormSchema, // the shape of your form. eg { name: string, description: string, curoId: string | null }
  ComponentProps, // props that the component needs, eg { reviewId: string, accountId: string }
  CustomFormField extends FormField = FormField // if you want to have extra fields on the default form field
>(
  WrappedComponent: React.FC<
    ComponentProps &
      AutoFormProps<FormSchema, CustomFormField & Partial<FormikSharedConfig>>
  >,
  formikProps?: FormikSharedConfig,
  otherProps: OtherFixedProps = {
    debugPosition: 'bottom',
  }
) {
  const Wrapper = (
    props: ComponentProps &
      AutoFormProps<FormSchema, CustomFormField> &
      OtherRuntimeProps &
      Partial<FormikSharedConfig>
  ) => {
    const {debug} = useDebugContext();

    const debugComponent = debug?.forms && (
      <AutoFormDebug
        debugChildrenValue={props.debugChildrenValue}
        debugChildren={props.debugChildren}
      />
    );
    if (!debugComponent) {
      return <WrappedComponent {...props} />;
    }
    if (otherProps.debugPosition === 'left') {
      return (
        <Box display="flex">
          <Box>{debugComponent}</Box>
          <WrappedComponent {...props} />
        </Box>
      );
    }
    if (otherProps.debugPosition === 'right') {
      return (
        <Box display="flex" justifyContent="space-between">
          <Box mr={2} flex={1}>
            <WrappedComponent {...props} />
          </Box>

          <Box width={500}>{debugComponent}</Box>
        </Box>
      );
    }
    return (
      <>
        {otherProps.debugPosition === 'top' ? debugComponent : null}
        <WrappedComponent {...props} />
        {otherProps.debugPosition === 'bottom' ? debugComponent : null}
      </>
    );
  };

  return withFormik<
    ComponentProps &
      AutoFormProps<FormSchema, CustomFormField> &
      OtherRuntimeProps &
      Partial<FormikSharedConfig>,
    FormikValues
  >({
    validate: (
      values,
      props: ComponentProps & AutoFormProps<FormSchema, CustomFormField>
    ) => {
      if (props.validate) {
        const errors = props.validate(values as any as FormSchema);
        return errors;
      }
    },
    validateOnBlur: true,
    enableReinitialize: formikProps?.enableReinitialize,
    validateOnChange: true,
    validateOnMount: false,
    mapPropsToValues: ({initialValueOverrides}: FormikValues) =>
      initialValueOverrides ?? {},
    validationSchema: ({
      validationSchema,
    }: ComponentProps & AutoFormProps<FormSchema, CustomFormField>) =>
      validationSchema ?? Yup.object().shape({}),
    async handleSubmit(values, bag) {
      const asciiVals = {} as any;
      Object.entries(values).forEach(([key, val]) => {
        (asciiVals as any)[key] = typeof val === 'string' ? cleanForCuro(val) : val;
      });
      if (bag.props.submit) {
        return bag.props.submit(values as any as FormSchema);
      }
    },
    ...formikProps,
  })(Wrapper);
}

export const FormReadOnlyContext = React.createContext<{
  readOnly: boolean;
  emptyValText: string;
}>({
  readOnly: false,
  emptyValText: 'Undefined',
});

export const FormReadOnly: React.FC<
  PropsWithChildren<{readOnly: boolean; emptyValText?: string}>
> = ({readOnly = true, children, emptyValText = 'Undefined'}) => {
  const value = useMemo(() => {
    return {readOnly, emptyValText};
  }, [readOnly, emptyValText]);
  return (
    <FormReadOnlyContext.Provider value={value}>{children}</FormReadOnlyContext.Provider>
  );
};

export default withAutoFormNew;
